import * as yup from 'yup';

import { SKU } from 'types/sku';

export type EquipmentCountRow = {
  sku: string;
  skuName: string;
  itemCount: number;
};

/**
 * The rows for the Equipment section of a hub count (ClickUp 86bbmcwfb): one
 * per equipment SKU, sorted by name, starting at 0.
 *
 * Every count already saved on the count being edited keeps its row, even if
 * that SKU has since been suspended or deleted or the SKU list has not loaded
 * yet. The form saves the whole array back, so a dropped row would erase that
 * number. Suspended SKUs with nothing saved are left out, as the other
 * sections do.
 */
export const buildEquipmentCountRows = (
  equipmentSkus: SKU[],
  saved: { sku: string; itemCount: number; skuName?: string }[] = [],
): EquipmentCountRow[] => {
  const savedCounts = new Map(saved.map((row) => [row.sku, row.itemCount]));
  const knownSkus = new Set(equipmentSkus.map((sku) => sku._id));

  const rows = equipmentSkus
    .filter((sku) => sku.isSuspended !== true || savedCounts.has(sku._id))
    .map((sku) => ({
      sku: sku._id,
      skuName: sku.name || '',
      itemCount: savedCounts.get(sku._id) ?? 0,
    }));

  const orphans = saved
    .filter((row) => !knownSkus.has(row.sku))
    .map((row) => ({
      sku: row.sku,
      skuName: row.skuName || '',
      itemCount: row.itemCount,
    }));

  return [...rows, ...orphans].sort((a, b) =>
    a.skuName.localeCompare(b.skuName),
  );
};

export const equipmentCountSchema = yup.array().of(
  yup.object().shape({
    itemCount: yup
      .number()
      .typeError('Count is required')
      .min(0, 'Count cannot be negative')
      .required('Count is required'),
  }),
);
